import { nanoid } from 'nanoid'
import * as argon2 from 'argon2'
import * as jwt from 'jsonwebtoken'
import {
  Principal,
  Scope,
  IssueApiKeyInput,
  IssueApiKeyResult,
  VerifyApiKeyResult,
  Session,
  Entitlement,
  Plan,
  Organization,
  AuthEvent,
  RateLimit,
  StorageAdapter,
  BillingAdapter,
  CacheAdapter,
  ApiKeyRecord,
  SessionRecord,
  OrganizationRecord,
  AuthProvider,
  OAuthCallbackResult
} from './types'
import { OAuthManager, OAuthTokenResponse, OAuthProfile } from './oauth'
import {
  ValidationError,
  AuthenticationError,
  AuthorizationError,
  NotFoundError,
  OAuthError,
  ProviderNotFoundError,
  SessionError,
  ApiKeyError,
  TokenError,
  DatabaseError,
  toAuthError
} from './errors'

export class AuthCore {
  private storage: StorageAdapter
  private billing?: BillingAdapter
  private cache?: CacheAdapter
  private jwtSecret: string
  private plans: Map<string, Plan> = new Map()
  private oauthManager: OAuthManager
  private providers: Map<string, AuthProvider> = new Map()

  constructor(
    storage: StorageAdapter,
    options: {
      billing?: BillingAdapter
      cache?: CacheAdapter
      jwtSecret: string
      plans?: Plan[]
      providers?: AuthProvider[]
    }
  ) {
    this.storage = storage
    this.billing = options.billing
    this.cache = options.cache
    this.jwtSecret = options.jwtSecret
    this.oauthManager = new OAuthManager(storage)

    if (options.plans) {
      options.plans.forEach(plan => this.plans.set(plan.id, plan))
    }

    if (options.providers) {
      options.providers.forEach(provider => this.providers.set(provider.id, provider))
    }
  }

  async initialize(): Promise<void> {
    await this.storage.connect()
    if (this.billing) await this.billing.connect()
    if (this.cache) await this.cache.connect()
  }

  async cleanup(): Promise<void> {
    await this.storage.disconnect()
    if (this.billing) await this.billing.disconnect()
    if (this.cache) await this.cache.disconnect()
  }

  // API Key Management
  async issueApiKey(input: IssueApiKeyInput): Promise<IssueApiKeyResult> {
    const id = nanoid()
    const key = this.generateApiKey(input.prefix || 'kit')
    const hash = await this.hashApiKey(key)
    const lastFour = key.slice(-4)

    const record: Omit<ApiKeyRecord, 'id' | 'createdAt'> = {
      principalId: input.principalId,
      hash,
      prefix: input.prefix || 'kit',
      lastFour,
      scopes: input.scopes,
      metadata: input.metadata,
      expiresAt: input.expiresAt,
      updatedAt: new Date()
    }

    const created = await this.storage.createApiKey(record)

    // Emit event
    await this.emitEvent({
      type: 'apikey.created',
      principalId: input.principalId,
      data: { keyId: id, scopes: input.scopes },
      timestamp: new Date()
    })

    return {
      id: created.id,
      key,
      prefix: created.prefix,
      lastFour: created.lastFour,
      expiresAt: created.expiresAt,
      createdAt: created.createdAt
    }
  }

  async verifyApiKey(presented: string): Promise<VerifyApiKeyResult> {
    if (!presented || typeof presented !== 'string') {
      throw new ValidationError('API key must be a non-empty string')
    }

    try {
      const [prefix, ...parts] = presented.split('_')
      if (parts.length < 1) {
        throw new ApiKeyError('Invalid API key format', {
          format: 'prefix_randomstring'
        })
      }

      const lastFour = parts[parts.length - 1].slice(-4)

      // Look up candidates by prefix + lastFour (stable selector)
      const candidates = await this.storage.getApiKeysByPrefixAndLastFour(prefix, lastFour)

      if (candidates.length === 0) {
        throw new ApiKeyError('API key not found')
      }

      // Check cache first for any of the candidates
      if (this.cache) {
        for (const candidate of candidates) {
          const cached = await this.cache.get(`apikey:${candidate.id}`)
          if (cached) {
            return cached
          }
        }
      }

      // Verify the presented key against stored hashes
      let validRecord: ApiKeyRecord | null = null
      for (const candidate of candidates) {
        const isValid = await this.verifyApiKeyHash(presented, candidate.hash)
        if (isValid) {
          validRecord = candidate
          break
        }
      }

      if (!validRecord) {
        throw new ApiKeyError('API key verification failed')
      }

      // Check expiration
      if (validRecord.expiresAt && validRecord.expiresAt < new Date()) {
        throw new ApiKeyError('API key has expired', {
          expiredAt: validRecord.expiresAt
        })
      }

      // Get principal info
      const principal = await this.resolvePrincipal(validRecord.principalId)
      if (!principal) {
        throw new NotFoundError('Principal', validRecord.principalId)
      }

      const result: VerifyApiKeyResult = {
        valid: true,
        principalId: validRecord.principalId,
        scopes: validRecord.scopes,
        plan: principal.plan,
        orgId: principal.orgId,
        keyId: validRecord.id,
        expiresAt: validRecord.expiresAt,
        rateLimit: await this.getRateLimit(principal)
      }

      // Cache the result
      if (this.cache) {
        await this.cache.set(`apikey:${validRecord.id}`, result, 300) // 5 minutes
      }

      return result
    } catch (error) {
      if (error instanceof ApiKeyError || error instanceof ValidationError) {
        throw error
      }
      throw toAuthError(error)
    }
  }

  async revokeApiKey(id: string): Promise<void> {
    if (!id || typeof id !== 'string') {
      throw new ValidationError('API key ID must be a non-empty string')
    }

    const record = await this.storage.getApiKey(id)
    if (!record) {
      throw new NotFoundError('API key', id)
    }

    await this.storage.deleteApiKey(id)

    // Invalidate cache
    if (this.cache) {
      await this.cache.del(`apikey:${record.hash}`)
    }

    // Emit event
    await this.emitEvent({
      type: 'apikey.revoked',
      principalId: record.principalId,
      data: { keyId: id },
      timestamp: new Date()
    })
  }

  async listApiKeys(principalId: string): Promise<ApiKeyRecord[]> {
    return this.storage.listApiKeys(principalId)
  }

  // Session Management
  async createSession(userId: string, orgId?: string): Promise<Session> {
    const id = nanoid()
    const principal = await this.resolvePrincipal(orgId || userId)
    if (!principal) {
      throw new Error('Principal not found')
    }

    const session: Omit<SessionRecord, 'id' | 'createdAt'> = {
      userId,
      orgId,
      plan: principal.plan,
      entitlements: principal.entitlements,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
      metadata: {},
      updatedAt: new Date()
    }

    const created = await this.storage.createSession(session)

    return {
      id: created.id,
      userId: created.userId,
      orgId: created.orgId,
      plan: created.plan,
      entitlements: created.entitlements,
      expiresAt: created.expiresAt,
      createdAt: created.createdAt,
      metadata: created.metadata
    }
  }

  async getSession(id: string): Promise<Session | null> {
    if (!id || typeof id !== 'string') {
      throw new ValidationError('Session ID must be a non-empty string')
    }

    const record = await this.storage.getSession(id)
    if (!record) {
      throw new SessionError('Session not found', { sessionId: id })
    }

    // Check expiration
    if (record.expiresAt < new Date()) {
      await this.storage.deleteSession(id)
      throw new SessionError('Session has expired', {
        sessionId: id,
        expiredAt: record.expiresAt
      })
    }

    return {
      id: record.id,
      userId: record.userId,
      orgId: record.orgId,
      plan: record.plan,
      entitlements: record.entitlements,
      expiresAt: record.expiresAt,
      createdAt: record.createdAt,
      metadata: record.metadata
    }
  }

  async refreshSession(id: string): Promise<Session | null> {
    const session = await this.getSession(id)
    if (!session) {
      return null
    }

    // Update entitlements from current principal
    const principal = await this.resolvePrincipal(session.orgId || session.userId)
    if (!principal) {
      return null
    }

    const updated = await this.storage.updateSession(id, {
      plan: principal.plan,
      entitlements: principal.entitlements,
      updatedAt: new Date()
    })

    return {
      id: updated.id,
      userId: updated.userId,
      orgId: updated.orgId,
      plan: updated.plan,
      entitlements: updated.entitlements,
      expiresAt: updated.expiresAt,
      createdAt: updated.createdAt,
      metadata: updated.metadata
    }
  }

  async deleteSession(id: string): Promise<void> {
    await this.storage.deleteSession(id)
  }

  /**
   * Generate a signed JWT for a valid, non-expired session
   */
  async generateSessionToken(sessionId: string): Promise<string> {
    const record = await this.storage.getSession(sessionId)
    if (!record) {
      throw new SessionError('Session not found', { sessionId })
    }
    if (record.expiresAt < new Date()) {
      // maintain parity with getSession behavior
      await this.storage.deleteSession(sessionId)
      throw new SessionError('Session has expired', { sessionId, expiredAt: record.expiresAt })
    }

    const payload = { sessionId: record.id, userId: record.userId, orgId: record.orgId }
    const expSeconds = Math.floor(record.expiresAt.getTime() / 1000)
    return jwt.sign(payload, this.jwtSecret, { expiresIn: expSeconds - Math.floor(Date.now() / 1000) })
  }

  // Entitlements and Plans
  async resolveEntitlements(principalId: string): Promise<{ plan: string; entitlements: Scope[] }> {
    const principal = await this.resolvePrincipal(principalId)
    if (!principal) {
      throw new Error('Principal not found')
    }

    return {
      plan: principal.plan || 'free',
      entitlements: principal.entitlements
    }
  }

  async hasEntitlement(principalId: string, scope: Scope): Promise<boolean> {
    const { entitlements } = await this.resolveEntitlements(principalId)
    return entitlements.includes(scope)
  }

  async addPlan(plan: Plan): Promise<void> {
    this.plans.set(plan.id, plan)
  }

  async getPlan(id: string): Promise<Plan | null> {
    return this.plans.get(id) || null
  }

  async listPlans(): Promise<Plan[]> {
    return Array.from(this.plans.values())
  }

  // Organization Management
  async createOrganization(data: {
    name: string
    plan: string
    ownerId: string
    seats?: number
    metadata?: Record<string, any>
  }): Promise<Organization> {
    const id = nanoid()
    const org: Omit<OrganizationRecord, 'id' | 'createdAt' | 'updatedAt'> = {
      name: data.name,
      plan: data.plan,
      seats: data.seats || 1,
      members: [{
        userId: data.ownerId,
        role: 'owner',
        joinedAt: new Date()
      }],
      metadata: data.metadata
    }

    const created = await this.storage.createOrganization(org)

    // Emit event
    await this.emitEvent({
      type: 'org.created',
      principalId: data.ownerId,
      orgId: id,
      data: { orgName: data.name, plan: data.plan },
      timestamp: new Date()
    })

    return {
      id: created.id,
      name: created.name,
      plan: created.plan,
      seats: created.seats,
      members: created.members,
      metadata: created.metadata,
      createdAt: created.createdAt,
      updatedAt: created.updatedAt
    }
  }

  async getOrganization(id: string): Promise<Organization | null> {
    const record = await this.storage.getOrganization(id)
    if (!record) {
      return null
    }

    return {
      id: record.id,
      name: record.name,
      plan: record.plan,
      seats: record.seats,
      members: record.members,
      metadata: record.metadata,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt
    }
  }

  // Rate Limiting
  private async getRateLimit(principal: Principal): Promise<RateLimit> {
    // Default rate limits based on plan
    const limits: Record<string, RateLimit> = {
      free: { limit: 1000, periodSec: 3600 }, // 1000 requests per hour
      basic: { limit: 10000, periodSec: 3600 }, // 10000 requests per hour
      pro: { limit: 100000, periodSec: 3600 }, // 100000 requests per hour
      enterprise: { limit: 1000000, periodSec: 3600 } // 1M requests per hour
    }

    return limits[principal.plan || 'free'] || limits.free
  }

  // Utility Methods
  private generateApiKey(prefix: string): string {
    const randomPart = nanoid(32)
    return `${prefix}_${randomPart}`
  }

  private async hashApiKey(key: string): Promise<string> {
    const { hashApiKey } = await import('./utils')
    return hashApiKey(key)
  }

  private async verifyApiKeyHash(presented: string, storedHash: string): Promise<boolean> {
    const { verifyApiKey } = await import('./utils')
    return verifyApiKey(presented, storedHash)
  }

  private async resolvePrincipal(id: string): Promise<Principal | null> {
    // Try to get from cache first
    if (this.cache) {
      const cached = await this.cache.get(`principal:${id}`)
      if (cached) {
        return cached
      }
    }

    // Check if it's an organization
    const org = await this.getOrganization(id)
    if (org) {
      const plan = await this.getPlan(org.plan)
      const principal: Principal = {
        id: org.id,
        type: 'org',
        plan: org.plan,
        entitlements: plan?.entitlements || [],
        metadata: org.metadata
      }

      // Cache the result
      if (this.cache) {
        await this.cache.set(`principal:${id}`, principal, 300) // 5 minutes
      }

      return principal
    }

    // Try to get user from storage
    const user = await this.storage.getUser(id)
    if (user) {
      const principal: Principal = {
        id: user.id,
        type: 'user',
        plan: user.plan,
        entitlements: user.entitlements,
        metadata: user.metadata
      }

      // Cache the result
      if (this.cache) {
        await this.cache.set(`principal:${id}`, principal, 300) // 5 minutes
      }

      return principal
    }

    // User not found - return null instead of default entitlements
    return null
  }

  private async emitEvent(event: AuthEvent): Promise<void> {
    await this.storage.emitEvent(event)
  }

  // OAuth Methods
  /**
   * Generate OAuth authorization URL for a provider
   */
  getOAuthAuthorizationUrl(
    providerId: string,
    redirectUri: string,
    usePKCE: boolean = true
  ): { url: string; state: string; codeVerifier?: string } {
    if (!providerId || typeof providerId !== 'string') {
      throw new ValidationError('Provider ID must be a non-empty string')
    }

    if (!redirectUri || typeof redirectUri !== 'string') {
      throw new ValidationError('Redirect URI must be a valid URL')
    }

    const provider = this.providers.get(providerId)
    if (!provider) {
      throw new ProviderNotFoundError(providerId)
    }

    if (provider.type !== 'oauth') {
      throw new OAuthError(
        `Provider ${providerId} is not configured as an OAuth provider`,
        providerId,
        { actualType: provider.type }
      )
    }

    try {
      return this.oauthManager.generateAuthorizationUrl(provider, redirectUri, '', usePKCE)
    } catch (error) {
      throw toAuthError(error)
    }
  }

  /**
   * Handle OAuth callback and create/update user session
   */
  async handleOAuthCallback(
    providerId: string,
    code: string,
    state: string,
    codeVerifier?: string
  ): Promise<OAuthCallbackResult> {
    if (!providerId || typeof providerId !== 'string') {
      throw new ValidationError('Provider ID must be a non-empty string')
    }

    if (!code || typeof code !== 'string') {
      throw new ValidationError('Authorization code must be a non-empty string')
    }

    if (!state || typeof state !== 'string') {
      throw new ValidationError('State parameter must be a non-empty string')
    }

    const provider = this.providers.get(providerId)
    if (!provider) {
      throw new ProviderNotFoundError(providerId)
    }

    if (provider.type !== 'oauth') {
      throw new OAuthError(
        `Provider ${providerId} is not configured as an OAuth provider`,
        providerId
      )
    }

    try {
      // Exchange code for tokens
      const { tokens } = await this.oauthManager.handleCallback(
        provider,
        code,
        state,
        codeVerifier
      )

      // Get user profile from OAuth provider
      const profile = await this.oauthManager.getUserProfile(provider, tokens.accessToken)

      // Get or create user from OAuth profile
      const user = await this.getOrCreateOAuthUser(providerId, profile)

      // Create session for user
      const session = await this.createSession(user.id)

      return {
        userId: user.id,
        session,
        profile,
        tokens
      }
    } catch (error) {
      if (error instanceof AuthenticationError || error instanceof OAuthError) {
        throw error
      }

      if (error instanceof Error && error.message.includes('PKCE')) {
        throw new OAuthError('PKCE verification failed', providerId, {
          reason: error.message
        })
      }

      if (error instanceof Error && error.message.includes('state')) {
        throw new OAuthError('State validation failed - possible CSRF attack', providerId)
      }

      if (error instanceof Error && error.message.includes('token')) {
        throw new OAuthError('Token exchange failed', providerId, {
          reason: error.message
        })
      }

      throw toAuthError(error)
    }
  }

  /**
   * Refresh OAuth access token
   */
  async refreshOAuthToken(
    providerId: string,
    refreshToken: string
  ): Promise<OAuthTokenResponse> {
    const provider = this.providers.get(providerId)
    if (!provider) {
      throw new Error(`OAuth provider ${providerId} not found`)
    }

    if (provider.type !== 'oauth') {
      throw new Error(`Provider ${providerId} is not an OAuth provider`)
    }

    return this.oauthManager.refreshAccessToken(provider, refreshToken)
  }

  /**
   * Register a provider (for dynamic provider setup)
   */
  registerProvider(provider: AuthProvider): void {
    if (this.providers.has(provider.id)) {
      throw new Error('Provider already registered')
    }
    this.providers.set(provider.id, provider)
  }

  /**
   * Get registered provider
   */
  getProvider(providerId: string): AuthProvider | undefined {
    return this.providers.get(providerId)
  }

  /**
   * List all registered providers
   */
  listProviders(): AuthProvider[] {
    return Array.from(this.providers.values())
  }

  /**
   * List OAuth providers only
   */
  listOAuthProviders(): AuthProvider[] {
    return Array.from(this.providers.values()).filter(p => p.type === 'oauth')
  }

  // User Management Methods
  /**
   * Create a new user
   */
  async createUser(input: {
    email?: string
    name?: string
    picture?: string
    plan?: string
    entitlements?: Scope[]
    metadata?: Record<string, any>
  }): Promise<import('./types').User> {
    const user = await this.storage.createUser({
      email: input.email,
      name: input.name,
      picture: input.picture,
      plan: input.plan || 'free',
      entitlements: input.entitlements || [],
      metadata: input.metadata
    })

    // Emit event
    await this.emitEvent({
      type: 'user.created',
      principalId: user.id,
      data: {
        email: user.email,
        name: user.name,
        plan: user.plan
      },
      timestamp: new Date()
    })

    return user
  }

  /**
   * Get user by ID
   */
  async getUser(id: string): Promise<import('./types').User | null> {
    return this.storage.getUser(id)
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<import('./types').User | null> {
    return this.storage.getUserByEmail(email)
  }

  /**
   * Get or create user from OAuth profile
   */
  async getOrCreateOAuthUser(
    provider: string,
    profile: OAuthProfile
  ): Promise<import('./types').User> {
    // Try to find existing user by OAuth link
    let user = await this.storage.getUserByOAuth(provider, profile.sub)

    if (user) {
      return user
    }

    // Try to find user by email if available
    if (profile.email) {
      user = await this.storage.getUserByEmail(profile.email)
      if (user) {
        // Link OAuth account to existing user
        return await this.storage.linkOAuthAccount(user.id, provider, {
          provider,
          sub: profile.sub,
          email: profile.email,
          name: profile.name,
          linkedAt: new Date()
        })
      }
    }

    // Create new user from OAuth profile
    const newUser = await this.createUser({
      email: profile.email,
      name: profile.name,
      picture: profile.picture,
      plan: 'free',
      entitlements: ['api.read'],
      metadata: {
        emailVerified: profile.emailVerified
      }
    })

    // Link OAuth account
    return await this.storage.linkOAuthAccount(newUser.id, provider, {
      provider,
      sub: profile.sub,
      email: profile.email,
      name: profile.name,
      linkedAt: new Date()
    })
  }

  /**
   * Update user
   */
  async updateUser(
    id: string,
    input: {
      email?: string
      name?: string
      picture?: string
      plan?: string
      entitlements?: Scope[]
      metadata?: Record<string, any>
    }
  ): Promise<import('./types').User> {
    const user = await this.storage.updateUser(id, input)

    // Emit event
    await this.emitEvent({
      type: 'user.updated',
      principalId: id,
      data: {
        email: user.email,
        name: user.name,
        plan: user.plan,
        entitlements: user.entitlements
      },
      timestamp: new Date()
    })

    // Invalidate cache
    if (this.cache) {
      await this.cache.del(`principal:${id}`)
    }

    return user
  }

  /**
   * Update user entitlements
   */
  async updateUserEntitlements(
    id: string,
    entitlements: Scope[]
  ): Promise<import('./types').User> {
    return this.updateUser(id, { entitlements })
  }

  /**
   * Update user plan (and corresponding entitlements)
   */
  async updateUserPlan(id: string, planId: string): Promise<import('./types').User> {
    const plan = await this.getPlan(planId)
    if (!plan) {
      throw new Error(`Plan ${planId} not found`)
    }

    return this.updateUser(id, {
      plan: planId,
      entitlements: plan.entitlements
    })
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await this.storage.deleteUser(id)

    // Emit event
    await this.emitEvent({
      type: 'user.deleted',
      principalId: id,
      data: { userId: id },
      timestamp: new Date()
    })

    // Invalidate cache
    if (this.cache) {
      await this.cache.del(`principal:${id}`)
    }
  }
}
